/**
 * AddUserForm — add a driver or office user to this hauler tenant.
 * Quick 260814-k6a / ClickUp 86bbed5r5.
 *
 * Two shapes behind one role picker:
 *   Driver / Lead Driver → name + phone. Signs in by SMS code. No email field —
 *     the address is synthesized server-side from the phone and is never
 *     something a human types or needs to know.
 *   Dispatcher / Dispatch Manager → name + email. Signs in with email + password
 *     and is emailed a temporary one. No phone field.
 *
 * NO "+1" APPEARS ANYWHERE (86bbed5r5 item 5) — not in the label, the
 * placeholder, the value, or the hint. The field takes a plain 10-digit US
 * number; the server canonicalizes to E.164 using the same shared helper the
 * mobile login screen uses. Displaying "+1" here would also be actively
 * misleading, since a driver never types it when logging in either.
 */

import { useState } from 'react';
import { canonicalizePhone, formatPhoneForDisplay } from '@lodex-pro/shared/phone';
import {
  CREATABLE_USER_ROLES,
  isDriverRole,
  useCreateUser,
  type CreatableUserRole,
  type CreateUserResult,
} from './use-create-user';
import { apiErrorText } from '@lodex-pro/web-shared';

export interface AddUserFormProps {
  readonly onCreated: (result: CreateUserResult) => void;
  readonly onCancel: () => void;
  /**
   * Restrict which roles this instance may create. Defaults to all four.
   *
   * When exactly ONE role is allowed the picker is hidden entirely and the role
   * is locked — the surface already says what it creates, so a one-option
   * dropdown is noise. That is how the Fleet → Drivers tab mounts it: a
   * "New driver" button inside a section titled Drivers should not offer to
   * create a dispatcher.
   *
   * Cosmetic only. The server re-validates the role against its own
   * CREATABLE_ROLES allowlist on every request — restricting the picker narrows
   * the UI, never the security boundary.
   */
  readonly allowedRoles?: readonly CreatableUserRole[];
}

export function AddUserForm({
  onCreated,
  onCancel,
  allowedRoles,
}: AddUserFormProps): React.ReactElement {
  const roleOptions =
    allowedRoles === undefined
      ? CREATABLE_USER_ROLES
      : CREATABLE_USER_ROLES.filter((r) => allowedRoles.includes(r.value));
  const lockedRole = roleOptions.length === 1 ? roleOptions[0]?.value : undefined;

  const [role, setRole] = useState<CreatableUserRole>(
    lockedRole ?? roleOptions[0]?.value ?? 'DRIVER',
  );
  const [fullName, setFullName] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const driver = isDriverRole(role);
  const canonicalPhone = canonicalizePhone(phoneRaw);

  const mutation = useCreateUser((result) => {
    setFullName('');
    setPhoneRaw('');
    setEmail('');
    setValidationError(null);
    onCreated(result);
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setValidationError(null);

    if (fullName.trim() === '') {
      setValidationError('Enter the person’s full name.');
      return;
    }
    if (driver) {
      if (canonicalPhone === '') {
        // Same rejection the login screen gives, for the same reason.
        setValidationError('Enter a 10-digit US mobile number.');
        return;
      }
      mutation.mutate({ role, fullName: fullName.trim(), phone: phoneRaw });
      return;
    }
    if (email.trim() === '') {
      setValidationError('Enter a work email address.');
      return;
    }
    mutation.mutate({ role, fullName: fullName.trim(), email: email.trim() });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {/* Hidden when only one role is allowed — the surface already says what it
          creates. See allowedRoles on the props. */}
      {lockedRole === undefined && (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Role</span>
          <select
            aria-label="Role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value as CreatableUserRole);
              setValidationError(null);
            }}
            className="min-h-[44px] rounded-md border border-border bg-background px-3"
          >
            {roleOptions.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">Full name</span>
        <input
          aria-label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="min-h-[44px] rounded-md border border-border bg-background px-3"
          autoComplete="name"
        />
      </label>

      {driver ? (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Mobile number</span>
          <input
            aria-label="Mobile number"
            // 10 digits. No "+1" — see the file header.
            placeholder="(555) 123-4567"
            inputMode="tel"
            autoComplete="tel-national"
            value={canonicalPhone !== '' ? formatPhoneForDisplay(canonicalPhone) : phoneRaw}
            onChange={(e) => {
              setPhoneRaw(e.target.value);
              setValidationError(null);
            }}
            className="min-h-[44px] rounded-md border border-border bg-background px-3"
          />
          <span className="text-xs text-muted-foreground">
            They’ll sign in with this number and a texted code. No password, and no email address
            needed.
          </span>
        </label>
      ) : (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Work email</span>
          <input
            aria-label="Work email"
            type="email"
            placeholder="name@company.com"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setValidationError(null);
            }}
            className="min-h-[44px] rounded-md border border-border bg-background px-3"
          />
          <span className="text-xs text-muted-foreground">
            They’ll sign in with this address and set a password on first login.
          </span>
        </label>
      )}

      {validationError !== null && (
        <p role="alert" className="text-sm text-destructive">
          {validationError}
        </p>
      )}
      {mutation.isError && (
        <p role="alert" className="text-sm text-destructive">
          {apiErrorText(mutation.error, 'Could not add this person. Try again.')}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="min-h-[44px] rounded-md bg-primary px-4 text-primary-foreground disabled:opacity-50"
        >
          {mutation.isPending ? 'Adding…' : 'Add person'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[44px] rounded-md border border-border px-4"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
